<script setup lang="ts">
import { computed } from "vue";

import actions from "~/actions";
import Tooltip from "~/components/dumb/Tooltip.vue";
import OptionComponent from "~/components/options/Option.vue";
import OptionEditDropdown from "~/components/options/OptionEditDropdown.vue";
import { styleConfig } from "~/constants/style";
import { CheckIcon, DotsHorizontalIcon, SwapIcon, XIcon } from "~/icons";
import { CommandId } from "~/shared/enums";
import type { MultiselectDropdownMenuItem, Option } from "~/shared/types";
import { useAppStore, usePageStore, useTenantStore } from "~/stores";
import { getChipColorsHex } from "~/utils/color";

const props = defineProps<{
  option: Option;
  selected: boolean;
  isChip?: boolean;
  isFilter?: boolean;
  replaceEnabled?: boolean;
}>();

const appStore = useAppStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();

const emit = defineEmits<{
  remove: [];
  swap: [duid: string];
  select: [item: MultiselectDropdownMenuItem];
}>();

const colorSet = computed(() => getChipColorsHex(props.option.colorHex, pageStore.theme));
const hoverColor = computed(() => (pageStore.theme === "dark" ? colorSet.value.back : styleConfig.colors["white/30"]));

const onContextMenu = (event: MouseEvent) => {
  if ((tenantStore.isDart && !pageStore.adminHidden && event.altKey) || pageStore.isPublicForm) {
    return;
  }

  appStore.openContextMenu(event as PointerEvent, actions.context.option(props.option), { maxHeightPixels: 566 });
};

const onOptionClick = (e: Event) => {
  e.stopImmediatePropagation();
  emit("select", {
    value: props.option.duid,
    label: props.option.title,
    selected: props.selected,
    component: OptionComponent,
  });
};
</script>

<template>
  <div
    v-if="selected && isChip"
    class="group/option relative"
    :style="{ '--background': hoverColor }"
    @contextmenu="onContextMenu">
    <OptionComponent :option="option" item-class="min-w-[40px]" show-ancestors>
      <div
        class="cursor-pointer rounded hover:bg-[--background]"
        :style="{ color: colorSet.fore }"
        @click="emit('remove')"
        @keydown.enter="emit('remove')">
        <XIcon class="icon-xs" />
      </div>
    </OptionComponent>
    <div
      class="absolute inset-y-px right-[19px] opacity-0 bg-lt"
      :class="!pageStore.isPublicForm && 'group-hover/option:opacity-100'">
      <div
        v-if="!pageStore.isPublicForm && !isFilter"
        class="flex size-full items-center py-0.5 pl-1 pr-0.5"
        :style="{ color: colorSet.fore, backgroundColor: colorSet.back }">
        <OptionEditDropdown :option="option">
          <div class="cursor-pointer rounded hover:bg-[--background]">
            <DotsHorizontalIcon class="cursor-pointer rounded icon-xs" />
          </div>
        </OptionEditDropdown>
      </div>
    </div>
  </div>

  <div
    v-else
    class="flex w-full items-center gap-2"
    @contextmenu="onContextMenu"
    @click.stop="onOptionClick"
    @keydown.enter="onOptionClick">
    <div class="flex-1 truncate">
      <OptionComponent :option="option" show-ancestors />
    </div>
    <CheckIcon v-if="pageStore.isMobile && selected" class="text-primary-base icon-md" />
    <template v-if="isFilter && !pageStore.isMobile">
      <Tooltip v-if="replaceEnabled" :command-id="CommandId.SWAP">
        <div
          class="flex items-center justify-center rounded opacity-0 icon-md hover:bg-hvy group-hover/swap-button:opacity-100"
          @click.stop="emit('swap', option.duid)"
          @keydown.enter.stop="emit('swap', option.duid)">
          <SwapIcon class="cursor-pointer text-md icon-xs" />
        </div>
      </Tooltip>
    </template>
    <OptionEditDropdown v-else-if="!pageStore.isMobile && !pageStore.isPublicForm" :option="option">
      <DotsHorizontalIcon class="cursor-pointer rounded text-lt icon-sm hover:bg-hvy focus:outline-none" />
    </OptionEditDropdown>
  </div>
</template>
